import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat/transcript_types';
import { normalizeVisibleText } from '@apps/web/features/phoebe_chat_v3/model/visible_text';

export type V3ActivityKind =
    | 'decision'
    | 'ehr'
    | 'general'
    | 'memory'
    | 'records'
    | 'replying';

export type V3ActivityStatus = {
    readonly kind: V3ActivityKind;
    readonly label: string;
    readonly detail: string | null;
};

export type V3Milestone = {
    readonly id: string;
    readonly kind: Exclude<V3ActivityKind, 'decision' | 'replying'>;
    readonly label: string;
    readonly detail: string | null;
};

export type V3ActivityCompletion = {
    readonly milestone: V3Milestone;
    // Durable side effects remain visible in the transcript. Read-only work
    // becomes part of the collapsible reasoning trace.
    readonly display: 'reasoning' | 'transcript';
};

export type V3ToolActivityPresentation = {
    readonly active: V3ActivityStatus | null;
    readonly completion: V3ActivityCompletion | null;
};

const HIDDEN_TOOL_NAMES = new Set([
    'ask_user_question',
    'load_skill',
    'request_user_decision',
    'show_dynamic_ui',
    'view_skills',
]);

const MEMORY_TOOL_NAMES = new Set([
    'get_caregiver_memory',
    'get_client_memory',
    'save_caregiver_memory_instruction',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function displayString(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }
    const normalized = normalizeVisibleText(value);
    if (normalized === null) {
        return null;
    }
    return normalized.length > 120
        ? `${normalized.slice(0, 119).trimEnd()}…`
        : normalized;
}

function sentenceCase(value: string): string {
    const first = value.charAt(0);
    const upper = first.toUpperCase();
    return upper === first ? value : `${upper}${value.slice(1)}`;
}

function sentenceCasePresentation(
    result: V3ToolActivityPresentation,
): V3ToolActivityPresentation {
    return {
        active:
            result.active === null
                ? null
                : {
                      ...result.active,
                      label: sentenceCase(result.active.label),
                  },
        completion:
            result.completion === null
                ? null
                : {
                      ...result.completion,
                      milestone: {
                          ...result.completion.milestone,
                          label: sentenceCase(
                              result.completion.milestone.label,
                          ),
                      },
                  },
    };
}

function operationId(entry: ToolExecutionEntry): string {
    return entry.callId ?? entry.commandId ?? entry.id;
}

function completedSuccessfully(entry: ToolExecutionEntry): boolean {
    if (!entry.isComplete || entry.error) {
        return false;
    }
    const status = entry.status?.toLowerCase() ?? '';
    return ![
        'cancelled',
        'denied',
        'failed',
        'incomplete',
        'rejected',
    ].includes(status);
}

function presentation({
    entry,
    kind,
    activeLabel,
    completedLabel,
    detail = null,
    completedDisplay = 'reasoning',
}: {
    readonly entry: ToolExecutionEntry;
    readonly kind: Exclude<V3ActivityKind, 'decision' | 'replying'>;
    readonly activeLabel: string;
    readonly completedLabel: string;
    readonly detail?: string | null;
    readonly completedDisplay?: V3ActivityCompletion['display'];
}): V3ToolActivityPresentation {
    const id = operationId(entry);
    if (!entry.isComplete) {
        return {
            active: {
                kind,
                label: activeLabel,
                detail,
            },
            completion: null,
        };
    }
    if (!completedSuccessfully(entry)) {
        return { active: null, completion: null };
    }
    return {
        active: null,
        completion: {
            display: completedDisplay,
            milestone: {
                id: `milestone:${id}`,
                kind,
                label: completedLabel,
                detail,
            },
        },
    };
}

function queryPresentation(
    entry: ToolExecutionEntry,
): V3ToolActivityPresentation {
    const args = isRecord(entry.arguments) ? entry.arguments : null;
    const description = displayString(args?.['description']);
    const label = description ?? 'Checking Phoebe records';
    return presentation({
        entry,
        kind: 'records',
        activeLabel: label,
        completedLabel: label,
    });
}

function bashPresentation(
    entry: ToolExecutionEntry,
): V3ToolActivityPresentation {
    const args = isRecord(entry.arguments) ? entry.arguments : null;
    const description = displayString(args?.['description']);
    if (description === null) {
        return { active: null, completion: null };
    }
    return presentation({
        entry,
        kind: 'general',
        activeLabel: description,
        completedLabel: description,
    });
}

function ehrPresentation(
    entry: ToolExecutionEntry,
): V3ToolActivityPresentation {
    const args = isRecord(entry.arguments) ? entry.arguments : null;
    const entityType = displayString(args?.['entity_type']);
    const isSingleClient = displayString(args?.['client_id']) !== null;
    const isSingleCaregiver = displayString(args?.['caregiver_id']) !== null;
    let noun = 'EHR data';
    if (isSingleClient) {
        noun = 'client record';
    } else if (isSingleCaregiver) {
        noun = 'caregiver record';
    } else if (entityType === 'client' || entityType === 'clients') {
        noun = 'client records';
    } else if (entityType === 'caregiver' || entityType === 'caregivers') {
        noun = 'caregiver records';
    } else if (entityType === 'shift' || entityType === 'shifts') {
        noun = 'EHR shifts';
    }
    const output = isRecord(entry.output) ? entry.output : null;
    const scope = displayString(output?.['scope']);
    if (
        entry.isComplete &&
        ((scope === 'client' && output?.['client_record_refreshed'] !== true) ||
            (scope === 'caregiver' &&
                output?.['caregiver_record_refreshed'] !== true))
    ) {
        return { active: null, completion: null };
    }
    return presentation({
        entry,
        kind: 'ehr',
        activeLabel: `Refreshing ${noun}`,
        completedLabel:
            scope === 'organization'
                ? 'Queued an EHR refresh'
                : `Refreshed ${noun}`,
    });
}

function pokePresentation(
    entry: ToolExecutionEntry,
): V3ToolActivityPresentation {
    const isCancellation = entry.name === 'cancel_poke';
    const output = isRecord(entry.output) ? entry.output : null;
    const fires = Array.isArray(output?.['fires']) ? output['fires'] : [];
    const firstFire = isRecord(fires[0]) ? fires[0] : null;
    const detail = displayString(firstFire?.['fire_at_local']);
    const reusedExistingSchedule =
        !isCancellation && output?.['created'] === false;
    return presentation({
        entry,
        kind: 'general',
        activeLabel: isCancellation
            ? 'Cancelling a check-in'
            : 'Scheduling a check-in',
        completedLabel: isCancellation
            ? 'Cancelled a check-in'
            : reusedExistingSchedule
              ? 'Kept the existing check-in'
              : 'Scheduled a check-in',
        detail,
        completedDisplay: 'transcript',
    });
}

function memoryPresentation(
    entry: ToolExecutionEntry,
): V3ToolActivityPresentation {
    const isWrite = entry.name.startsWith('save_');
    return presentation({
        entry,
        kind: 'memory',
        activeLabel: isWrite ? 'Updating memory' : 'Reviewing memory',
        completedLabel: isWrite ? 'Updated memory' : 'Reviewed memory',
    });
}

function sendChatAttachmentPresentation(
    entry: ToolExecutionEntry,
): V3ToolActivityPresentation {
    const args = isRecord(entry.arguments) ? entry.arguments : null;
    const output = isRecord(entry.output) ? entry.output : null;
    const receipt = isRecord(output?.['chat_attachment'])
        ? output['chat_attachment']
        : null;
    const description = displayString(args?.['description']);
    const filepath = displayString(args?.['filepath']);
    const storedFilename = displayString(receipt?.['filename']);
    const activeLabel = description ?? 'Attaching a file';
    const completedLabel = storedFilename
        ? `Attached ${storedFilename}`
        : filepath
          ? `Attached ${filepath.split('/').pop() ?? filepath}`
          : (description ?? 'Attached a file');
    return presentation({
        entry,
        kind: 'general',
        activeLabel,
        completedLabel,
    });
}

function toolActivityPresentation(
    entry: ToolExecutionEntry,
): V3ToolActivityPresentation {
    if (HIDDEN_TOOL_NAMES.has(entry.name)) {
        return { active: null, completion: null };
    }
    if (entry.name === 'query') {
        return queryPresentation(entry);
    }
    if (entry.name === 'run_bash') {
        return bashPresentation(entry);
    }
    if (entry.name === 'trigger_ehr_sync') {
        return ehrPresentation(entry);
    }
    if (entry.name === 'schedule_poke' || entry.name === 'cancel_poke') {
        return pokePresentation(entry);
    }
    if (entry.name === 'send_chat_attachment') {
        return sendChatAttachmentPresentation(entry);
    }
    if (MEMORY_TOOL_NAMES.has(entry.name)) {
        return memoryPresentation(entry);
    }
    return { active: null, completion: null };
}

export function getV3ToolActivityPresentation(
    entry: ToolExecutionEntry,
): V3ToolActivityPresentation {
    return sentenceCasePresentation(toolActivityPresentation(entry));
}
